/**
 * collection-view-toggle.js
 *
 * Grid / list switch for the category page toolbar.
 *
 * The chosen view is written to `data-collection-view` on <html>, NOT onto the
 * grid itself. That matters: Dawn's facets.js replaces the innerHTML of
 * #ProductGridContainer on every filter, sort and pagination change, so any
 * class set on the grid would be silently wiped the first time someone ticked a
 * filter. The <html> element survives those re-renders, and the CSS keys off it
 * by descendant selector.
 *
 * The preference persists in localStorage so it holds across collections and
 * across visits. localStorage is wrapped because Safari throws on access in
 * private browsing rather than returning null.
 */

const STORAGE_KEY = 'saq:collection-view';
const DEFAULT_VIEW = 'grid';

function readStoredView() {
  try {
    const stored = localStorage.getItem(STORAGE_KEY);
    return stored === 'list' || stored === 'grid' ? stored : null;
  } catch (e) {
    return null;
  }
}

function storeView(view) {
  try {
    localStorage.setItem(STORAGE_KEY, view);
  } catch (e) {
    /* private browsing — the toggle still works for this page view */
  }
}

class CollectionViewToggle extends HTMLElement {
  connectedCallback() {
    this.buttons = Array.from(this.querySelectorAll('[data-view]'));
    if (this.buttons.length === 0) return;

    this.apply(readStoredView() || DEFAULT_VIEW, false);

    this.buttons.forEach((button) => {
      button.addEventListener('click', () => this.apply(button.dataset.view, true));
    });
  }

  apply(view, persist) {
    document.documentElement.setAttribute('data-collection-view', view);

    this.buttons.forEach((button) => {
      const isActive = button.dataset.view === view;
      button.setAttribute('aria-pressed', isActive ? 'true' : 'false');
      button.classList.toggle('collection-view-toggle__button--active', isActive);
    });

    if (persist) storeView(view);
  }
}

if (!customElements.get('collection-view-toggle')) {
  customElements.define('collection-view-toggle', CollectionViewToggle);
}
